import React, { useCallback, useMemo, useState } from "react";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Button from "veneer/Button";
import Drawer from "veneer/Drawer";
import { showError, showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import { CheckFilledIcon, ErrorStopSignOutlinedIcon } from "veneer/SvgIcon";
import { Colors } from "veneer/Theme";
import Tooltip from "veneer/Tooltip";
import Typography from "veneer/Typography";

import { RELEASE_NOTES_URL } from "cortex/ui/src/constant/app";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { compareVersion } from "cortex/ui/src/fleet/view/device/utils";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { UPGRADE_DEVICES } from "cortex/ui/src/redux-store/devices/actions";

import BaseDrawer, {
  ErrorListItem,
  ListItem,
  ListWrapper,
  UpgradeDrawerProps,
  UpgradeEligibility,
  UpgradeRow,
} from "./BaseDrawer";

function getUpgradeEligibility(device: Device): UpgradeEligibility {
  if (device.device_update_disabled) {
    return {
      isValid: false,
      reason: "Remote updates are disabled for this device",
    };
  }

  if (
    device.network_version &&
    device.latest_version &&
    compareVersion(device.network_version, device.latest_version) > 0
  ) {
    return {
      isValid: false,
      reason: `Network settings restrict agent upgrade above version ${device.network_version}`,
    };
  }

  if (device.target_version) {
    return {
      isValid: false,
      reason: `Agent is pending upgrade to version ${device.target_version}`,
    };
  }

  const isOnLatestVersion = device.current_version === device.latest_version;
  if (isOnLatestVersion) {
    return {
      isValid: false,
      reason: "Agent is on the latest version and won't upgrade",
    };
  }

  return { isValid: true };
}

export default function UpgradeAgentDrawer({
  onCancel,
  open,
  selectedItems,
  setSelectedItems,
}: UpgradeDrawerProps) {
  const [submitted, setSubmitted] = useState(false);
  const upgradeRows: ReadonlyArray<UpgradeRow> = useMemo(() => {
    return selectedItems
      .filter((d) => d.is_agent)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((device) => {
        const eligibility = getUpgradeEligibility(device);
        return { device, ...eligibility };
      });
  }, [selectedItems]);

  const targetVersion = useMemo(() => {
    const versions = upgradeRows.map(
      ({ device }) => device.latest_version ?? "0.0.0",
    );

    versions.sort((a, b) => {
      const aParts = a.split(".").map(Number);
      const bParts = b.split(".").map(Number);
      for (let i = 0; i < 3; i++) {
        if (aParts[i] > bParts[i]) return -1;
        if (aParts[i] < bParts[i]) return 1;
      }
      return 0;
    });

    return versions[0];
  }, [upgradeRows]);

  const validDeviceIds = useMemo(() => {
    return upgradeRows.filter((r) => r.isValid).map((r) => r.device.id);
  }, [upgradeRows]);

  const dispatch = useAppDispatch();

  const handleUpgradeAgent = useCallback(async () => {
    if (validDeviceIds.length === 0) {
      return;
    }
    setSubmitted(true);
    try {
      await createPromiseApiAction(dispatch, UPGRADE_DEVICES, {
        deviceIds: validDeviceIds,
        targetVersion,
      });
      showSuccess(
        `Request processed successfully. Your ${validDeviceIds.length} agent(s) will be upgraded shortly.`,
      );
    } catch (e) {
      showError(
        "An error occurred while attempting to upgrade the selected agents.",
      );
    } finally {
      setSelectedItems([]);
      setSubmitted(false);
      onCancel();
    }
  }, [dispatch, onCancel, setSelectedItems, targetVersion, validDeviceIds]);

  const drawerHeader = (
    <>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={1}
      >
        <Box width="66%">
          <Typography color={Colors.gray1000} variant="body2">
            Review player release notes and monitor the performance of a limited
            set of players before a wider network rollout.
          </Typography>
        </Box>
        <Box minWidth="fit-content">
          <Button
            icon="openInNew"
            iconPosition="end"
            onClick={() => {
              window.open(RELEASE_NOTES_URL, "_blank");
            }}
            variant="text"
          >
            Release Notes
          </Button>
        </Box>
      </Stack>
      <Alert dense severity="warning" title="Double-check the player list">
        You will <b>not</b> be able to revert this change.
      </Alert>
    </>
  );

  return (
    <Drawer
      commitDisabled={validDeviceIds.length === 0 || submitted}
      commitLabel="Upgrade Agent"
      commitLoading={submitted}
      onCancel={onCancel}
      onClose={onCancel}
      onCommit={handleUpgradeAgent}
      open={open}
      padding={0}
      title="Upgrade Agent"
    >
      <BaseDrawer
        drawerHeader={drawerHeader}
        title={`Agent will upgrade to ${targetVersion}`}
      >
        {upgradeRows.map(({ device, isValid, reason }) => (
          <ListWrapper disablePadding key={device.id}>
            <UpgradeListItem
              device={device}
              isValid={isValid}
              reason={reason}
            />
          </ListWrapper>
        ))}
      </BaseDrawer>
    </Drawer>
  );
}

const UpgradeListItem = ({ device, isValid, reason }: UpgradeRow) => {
  const ListComponent = isValid ? ListItem : ErrorListItem;

  return (
    <ListComponent>
      <Stack alignItems="center" direction="row" spacing={2}>
        {isValid ? (
          <CheckFilledIcon color="success" />
        ) : (
          <ErrorStopSignOutlinedIcon color="error" />
        )}
        <Stack flex={1}>
          <Tooltip title={device.device_id}>
            <Typography color={Colors.gray1000} variant="body1">
              {device.name}
            </Typography>
          </Tooltip>
          <Typography variant="body2">
            Agent {device.current_version}
            {!isValid && reason && ` - ${reason}`}
          </Typography>
        </Stack>
      </Stack>
    </ListComponent>
  );
};
